import { ApplicationConfig, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { firstValueFrom, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { loadingInterceptor } from './core/interceptors/loading.interceptor';
import { AuthService } from './core/auth/auth.service';
import { FeatureFlagsService } from './core/services/feature-flags.service';

/**
 * Boot order (fail closed):
 * 1. Fetch Billing/Terms switches and clear any stale local ON
 * 2. Restore session (checkAuthStatus also refreshes switches again after auth)
 *
 * Guards must not run until this completes — features stay OFF until the server
 * answers with an explicit true.
 */
export function initializeApp(authService: AuthService, features: FeatureFlagsService) {
  return () =>
    firstValueFrom(
      features.load(true).pipe(
        catchError(() => of(null)),
        switchMap(() => authService.checkAuthStatus().pipe(catchError(() => of(false))))
      )
    );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([authInterceptor, errorInterceptor, loadingInterceptor])
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AuthService, FeatureFlagsService],
      multi: true
    }
  ]
};
